/*
Origin Core 1.0

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <variant-picker> that manages variant selection for Shopify products. 
It listens for change events, updates the selected option, dispatches custom events, and fetches updated product or variant data via AJAX. 
The component morphs the DOM to reflect changes, updates the main content or just the variant picker as needed, and synchronizes the browser URL with the selected variant. 
It supports combined listings and ensures only the latest fetch request is processed. 
The component is registered if not already defined.

*/

import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        //html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          document.querySelector('product-price').innerHTML = html.querySelector('product-price').innerHTML;
          
          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    // Preserve content of elements marked with data-preserve-content before morphing
    const preservedElements = new Map();
    const elementsToPreserve = Array.from(main.querySelectorAll('[data-preserve-content]'));
    elementsToPreserve.forEach((element, index) => {
      const preserveId = element.getAttribute('data-preserve-content') || '';
      const key = preserveId ? `${preserveId}-${index}` : `default-${index}`;
      preservedElements.set(key, { content: element.innerHTML, preserveId, index });
    });

    // Store old product IDs from product recommendations before morphing
    const oldProductRecommendations = Array.from(main.querySelectorAll('product-recommendations'));
    const oldProductIds = new Map();
    oldProductRecommendations.forEach((el) => {
      if (el.id && el.dataset.productId) {
        oldProductIds.set(el.id, el.dataset.productId);
      }
    });

    morph(main, newMain);

    // Restore preserved content after morphing
    const restoredElements = Array.from(main.querySelectorAll('[data-preserve-content]'));
    preservedElements.forEach((preserved, key) => {
      let targetElement = null;
      if (preserved.preserveId) {
        const matchingElements = restoredElements.filter(el => el.getAttribute('data-preserve-content') === preserved.preserveId);
        targetElement = matchingElements.length > preserved.index ? matchingElements[preserved.index] : matchingElements[0] ?? null;
      } else {
        const elementsWithoutId = restoredElements.filter(el => !el.getAttribute('data-preserve-content') || el.getAttribute('data-preserve-content') === '');
        targetElement = elementsWithoutId[preserved.index] ?? null;
      }
      if (targetElement) {
        const currentContent = targetElement.innerHTML.trim();
        if (!currentContent || currentContent.length < preserved.content.length) {
          targetElement.innerHTML = preserved.content;
        }
      }
    });

    // Re-initialize Alpine.js on the updated section
    if (window.Alpine && typeof Alpine.initTree === 'function') {
      Alpine.initTree(main);
    }

    // Re-initialize reviews.io rating snippet for newly loaded products
    if (typeof ratingSnippet === 'function') {
      ratingSnippet("ruk_rating_snippet", {
        store: "countrycollection",
        mode: "default",
        color: "#000000",
        linebreak: false,
        text: "Reviews",
        singularText: "Review",
        lang: "en",
        usePolaris: true,
        showEmptyStars: false,
      });
    }

    // Re-initialize Reviews.io full widget for newly loaded combined listing product
    const reviewsWidgetEl = main.querySelector('#ReviewsWidget');
    if (reviewsWidgetEl && typeof ReviewsWidget === 'function') {
      reviewsWidgetEl.innerHTML = '';
      new ReviewsWidget('#ReviewsWidget', {
        store: 'countrycollection',
        widget: 'polaris',
        options: {
          types: 'product_review',
          enable_sentiment_analysis: true,
          lang: 'en',
          layout: '',
          per_page: 15,
          store_review: { hide_if_no_results: false },
          third_party_review: { hide_if_no_results: false },
          product_review: {
            sku: reviewsWidgetEl.dataset.skus || '',
            hide_if_no_results: false,
          },
          questions: {
            hide_if_no_results: false,
            enable_ask_question: true,
            enable_ask_question_button_style: false,
            show_dates: true,
            grouping: '[Group questions by providing a grouping variable here or a specific product SKU]',
          },
          // ... (rest of your header/filtering/reviews/sentiment options unchanged)
          //Header settings:
          header:{
              enable_summary: true, //Show overall rating & review count
              enable_ratings: true,
              enable_attributes: true,
              enable_image_gallery: true, //Show photo & video gallery
              enable_percent_recommended: false, //Show what percentage of reviewers recommend it
              enable_write_review: true, //Show "Write Review" button
              enable_ask_question: false, //Show "Ask Question" button
              enable_sub_header: true, //Show subheader
              rating_decimal_places: 2,
              use_write_review_button:  false, //Show "be the first to leave a review" text as a button
              enable_if_no_results:  false, //Show header when there are no results
              show_review_title_field:  false, //Show review title field in write review form
          },

          //AI summary settings
          sentiment:{
              badge_text: 'Our Customers Say'
          },

          //Filtering settings:
          filtering:{
              enable: true, //Show filtering options
              enable_text_search: true, //Show search field
              enable_sorting: true, //Show sorting options (most recent, most popular)
              enable_product_filter: false, //Show product options filter
              enable_media_filter: true, //Show reviews with images/video/media options
              enable_overall_rating_filter: true, //Show overall rating breakdown filter
              enable_language_filter: false, // Filter by review language
              enable_language_filter_language_change: false, // Update widget language based on language selected
              enable_ratings_filters: true, //Show product attributes filter
              enable_attributes_filters: true, //Show author attributes filter
              enable_expanded_filters: false, //Show filters as a section instead of dropdown
          },

          //Review settings:
          reviews:{
              enable_avatar: true, //Show author avatar
              enable_reviewer_name:  true, //Show author name
              enable_reviewer_address:  true, //Show author location
              reviewer_address_format: 'city, country', //Author location display format
              enable_verified_badge: true, //Show "Verified Customer" badge
              enable_subscriber_badge: true, //Show "Verified Subscriber" badge
              review_content_filter: 'undefined', //Filter content
              enable_reviewer_recommends: true, //Show "I recommend it" badge
              enable_attributes: true, //Show author attributes
              enable_product_name: true, //Show display product name
              enable_link_product: false, //Link review to product page
              enable_review_title: undefined, //Show review title
              enable_replies: true, //Show review replies
              enable_images: true, //Show display review photos
              enable_ratings: true, //Show product attributes (additional ratings)
              enable_share: true, //Show share buttons
              enable_helpful_vote: true, //Show "was this helpful?" section
              enable_helpful_display: true, //Show how many times times review upvoted
              enable_report: true, //Show report button
              enable_date: true, //Show when review was published
              date_format: 'undefined', ////Review date format (undefined, 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD')
              date_location: 'undefined', //Review date location (undefined, 'top-right')
              enable_third_party_source: true, //Show third party source
              enable_duplicate_reviews: undefined, //Show multiple reviews from the same user
              combine_title_stars: true, //Combine review title and star rating in a single line

          },
        },
        //Translation settings
        translations: {
            'Verified Customer': 'Verified Customer'
        },
        //Style settings:
        styles: {
            //Base font size is a reference size for all text elements. When base value gets changed, all TextHeading and TexBody elements get proportionally adjusted.
            '--base-font-size': '16px',

            //Button styles (shared between buttons):
            '--common-button-font-family': 'inherit',
            '--common-button-font-size':'16px',
            '--common-button-font-weight':'500',
            '--common-button-letter-spacing':'0',
            '--common-button-text-transform':'none',
            '--common-button-vertical-padding':'10px',
            '--common-button-horizontal-padding':'20px',
            '--common-button-border-width':'2px',
            '--common-button-border-radius':'0px',

            //Primary button styles:
            '--primary-button-bg-color': '#0E1311',
            '--primary-button-border-color': '#0E1311',
            '--primary-button-text-color': '#ffffff',

            //Secondary button styles:
            '--secondary-button-bg-color': 'transparent',
            '--secondary-button-border-color': '#0E1311',
            '--secondary-button-text-color': '#0E1311',

            //Star styles:
            '--common-star-color': '#0E1311',
            '--common-star-disabled-color': 'rgba(0,0,0,0.25)',
            '--medium-star-size': '22px',
            '--small-star-size': '19px',

            //Heading styles:
            '--heading-text-color': '#0E1311',
            '--heading-text-font-weight': '600',
            '--heading-text-font-family': 'inherit',
            '--heading-text-line-height': '1.4',
            '--heading-text-letter-spacing': '0',
            '--heading-text-transform': 'none',

            //Body text styles:
            '--body-text-color': '#0E1311',
            '--body-text-font-weight': '400',
            '--body-text-font-family': 'inherit',
            '--body-text-line-height': '1.4',
            '--body-text-letter-spacing': '0',
            '--body-text-transform': 'none',

            //Input field styles:
            '--inputfield-text-font-family': 'inherit',
            '--input-text-font-size': '14px',
            '--inputfield-text-font-weight': '400',
            '--inputfield-text-color': '#0E1311',
            '--inputfield-border-color': 'rgba(0,0,0,0.2)',
            '--inputfield-background-color': 'transparent',
            '--inputfield-border-width': '1px',
            '--inputfield-border-radius': '0px',

            '--common-border-color': 'rgba(0,0,0,0.15)',
            '--common-border-width': '1px',
            '--common-sidebar-width': '190px',

            //Filters panel styles:
            '--filters-panel-bg-color': 'transparent',
            '--filters-panel-font-size': '16px',
            '--filters-panel-text-color': '16px',
            '--filters-panel-horizontal-padding': '0',
            '--filters-panel-vertical-padding': '0',

            //Slider indicator (for attributes) styles:
            '--slider-indicator-bg-color': 'rgba(0,0,0,0.1)',
            '--slider-indicator-button-color': '#0E1311',
            '--slider-indicator-width': '190px',

            //Badge styles:
            '--badge-icon-color': '#0E1311',
            '--badge-icon-font-size': 'inherit',
            '--badge-text-color': '#0E1311',
            '--badge-text-font-size': 'inherit',
            '--badge-text-letter-spacing': 'inherit',
            '--badge-text-transform': 'inherit',

            //Author styles:
            '--author-font-size': 'inherit',
            '--author-text-transform': 'none',

            //Author avatar styles:
            '--avatar-thumbnail-size': '60px',
            '--avatar-thumbnail-border-radius': '100px',
            '--avatar-thumbnail-text-color': '#0E1311',
            '--avatar-thumbnail-bg-color': 'rgba(0,0,0,0.1)',

            //Product photo or review photo styles:
            '--photo-video-thumbnail-size': '80px',
            '--photo-video-thumbnail-border-radius': '0px',

            //Media (photo & video) slider styles:
            '--mediaslider-scroll-button-icon-color': '#0E1311',
            '--mediaslider-scroll-button-bg-color': 'rgba(255, 255, 255, 0.85)',
            '--mediaslider-overlay-text-color': '#ffffff',
            '--mediaslider-overlay-bg-color': 'rgba(0, 0, 0, 0.8))',
            '--mediaslider-item-size': '110px',

            //Pagination & tabs styles (normal):
            '--pagination-tab-text-color': '#0E1311',
            '--pagination-tab-text-transform': 'none',
            '--pagination-tab-text-letter-spacing': '0',
            '--pagination-tab-text-font-size': '16px',
            '--pagination-tab-text-font-weight': '600',

            //Pagination & tabs styles (active):
            '--pagination-tab-active-text-color': '#0E1311',
            '--pagination-tab-active-text-font-weight': '600',
            '--pagination-tab-active-border-color': '#0E1311',
            '--pagination-tab-border-width': '3px',

            //AI summary styles
            '--sentiment-base-font-size': '15px',
            '--sentiment-panel-bg-color': 'transparent',
            '--sentiment-panel-border-size': '1px',
            '--sentiment-panel-border-color': 'rgba(0, 0, 0, 0.1)',
            '--sentiment-panel-border-radius': '0px',
            '--sentiment-panel-shadow-size': '0px',
            '--sentiment-panel-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--sentiment-heading-text-color': '#0E1311',
            '--sentiment-heading-text-font-weight': '600',
            '--sentiment-heading-text-font-family': 'inherit',
            '--sentiment-heading-text-line-height': '1.4',
            '--sentiment-heading-text-letter-spacing': '0',
            '--sentiment-heading-text-transform': 'none',
            '--sentiment-body-text-color': '#0E1311',
            '--sentiment-body-text-font-weight': '400',
            '--sentiment-body-text-font-family': 'inherit',
            '--sentiment-body-text-line-height': '1.4',
            '--sentiment-body-text-letter-spacing': '0',
            '--sentiment-body-text-transform': 'none',
            '--sentiment-panel-vertical-padding': '25px',
            '--sentiment-panel-horizontal-padding': '20px',
            '--sentiment-header-text-color': 'inherit',
            '--sentiment-header-text-font-size': '12px',
            '--sentiment-header-text-font-weight': 'inherit',
            '--sentiment-header-bg-color': 'rgba(0, 0, 0, 0.04)',
            '--sentiment-header-border-radius': '50px',
            '--sentiment-header-shadow-size': '0px',
            '--sentiment-header-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--sentiment-header-vertical-padding': '7px',
            '--sentiment-header-horizontal-padding': '10px',
            '--sentiment-pagination-tab-text-font-size': '13px',
            '--sentiment-pagination-tab-text-font-weight': '600',
            '--sentiment-pagination-tab-text-color': '#0E1311',
            '--sentiment-pagination-tab-text-transform': 'none',
            '--sentiment-pagination-tab-text-letter-spacing': '0',
            '--sentiment-pagination-tab-active-text-color': '#0E1311',
            '--sentiment-pagination-tab-active-text-font-weight': '600',
            '--sentiment-pagination-tab-active-border-color': '#0E1311',
            '--sentiment-pagination-tab-border-width': '3px',
        },
      });
    }


    // Reset and reload product recommendations if product ID changed
    // After morphing, check all product-recommendations components
    const newProductRecommendations = Array.from(main.querySelectorAll('product-recommendations'));
    newProductRecommendations.forEach((el) => {
      if (!el.id || !el.dataset.productId) return;

      const oldProductId = oldProductIds.get(el.id);
      const newProductId = el.dataset.productId;

      // If product ID changed, reset and reload recommendations
      if (oldProductId && oldProductId !== newProductId) {
        // Remove the attribute to allow reload
        el.removeAttribute('data-recommendations-performed');
        // Trigger MutationObserver by setting a temporary data attribute
        // This will cause the observer to detect the change and reload
        // Use requestAnimationFrame to ensure the mutation is detected after morph completes
        requestAnimationFrame(() => {
          el.setAttribute('data-trigger-reload', 'true');
          requestAnimationFrame(() => {
            el.removeAttribute('data-trigger-reload');
          });
        });
      } else if (!oldProductId && el.dataset.recommendationsPerformed === 'true') {
        // New component that might have been morphed in place with old data
        // Remove the attribute to ensure it can load
        el.removeAttribute('data-recommendations-performed');
      }
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
